import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Check as CheckIcon,
  ChevronDown as ChevronDownIcon,
  Inbox as InboxIcon,
  UserRound as UserIcon,
  UserRoundPlus as UnassignedIcon,
} from "lucide-react";
import type { ConversationListViewName } from "@multi-chat/shared";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu/index.js";
import { cn } from "../../lib/utils.js";

const VIEWS = ["ALL", "MINE", "UNASSIGNED"] as const satisfies readonly ConversationListViewName[];

const ICONS = {
  ALL: InboxIcon,
  MINE: UserIcon,
  UNASSIGNED: UnassignedIcon,
} as const;

export function InboxViewsMenu({
  view,
  onViewChange,
  showMine,
}: {
  view: ConversationListViewName;
  onViewChange: (view: ConversationListViewName) => void;
  showMine: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const items = useMemo(
    () => VIEWS.filter((item) => item !== "MINE" || showMine),
    [showMine],
  );
  const label = t(`inbox.workflow.views.${view}`);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex min-w-0 max-w-full items-center gap-1 rounded-lg px-1.5 py-1 text-base leading-tight font-semibold tracking-[-0.016em] text-inbox-strong hover:bg-inbox-hover focus-visible:outline-2 focus-visible:outline-primary rtl:tracking-normal"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={`${t("inbox.workflow.views.label")}: ${label}`}
          data-testid="inbox-views-trigger"
        >
          <span className="min-w-0 truncate">{label}</span>
          <ChevronDownIcon size={16} className="shrink-0 text-inbox-muted" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-56 rounded-xl border-inbox-line p-1.5 shadow-inbox-pop"
      >
        {items.map((item) => {
          const Icon = ICONS[item];
          const active = item === view;
          return (
            <DropdownMenuItem
              key={item}
              className="min-h-9 gap-2.5 rounded-lg px-2.5 text-[0.8125rem]"
              data-testid={`inbox-view-${item}`}
              onSelect={() => onViewChange(item)}
            >
              <Icon size={16} className="text-inbox-muted" aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate">{t(`inbox.workflow.views.${item}`)}</span>
              {active ? <CheckIcon size={16} className="text-primary" aria-hidden="true" /> : null}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
